import {
  arrayField,
  conditionRecord,
  extractFlexibleListRows,
  objectField,
  stringField,
  stringList,
} from "./parseFields";
import type {
  ComplianceMonitoredDbConfig,
  ComplianceMonitoredWorkload,
  ComplianceRule,
} from "./types";

export function indicatorKeysFromMapping(
  mapping: Record<string, unknown> | null,
): string[] {
  if (mapping == null) {
    return [];
  }
  return Object.keys(mapping).filter((key) => key.trim());
}

export function normalizeIndicatorMapping(
  mapping: Record<string, unknown> | null,
): Record<string, string[]> {
  if (mapping == null) {
    return {};
  }
  const normalized: Record<string, string[]> = {};
  for (const key of indicatorKeysFromMapping(mapping)) {
    normalized[key] = stringList(mapping[key]);
  }
  return normalized;
}

export function normalizeMonitoredWorkload(
  item: unknown,
): ComplianceMonitoredWorkload | null {
  if (item == null || typeof item !== "object" || Array.isArray(item)) {
    return null;
  }
  const record = item as Record<string, unknown>;
  const workload: ComplianceMonitoredWorkload = {
    resourceType: stringField(record, "resourceType", "resource_type"),
    region: stringField(record, "region"),
    clusterName: stringField(record, "clusterName", "cluster_name"),
    namespace: stringField(record, "namespace"),
    deployment: stringField(record, "deployment"),
  };
  return Object.values(workload).some(Boolean) ? workload : null;
}

export function normalizeMonitoredDbConfig(
  item: unknown,
): ComplianceMonitoredDbConfig | null {
  if (item == null || typeof item !== "object" || Array.isArray(item)) {
    return null;
  }
  const record = item as Record<string, unknown>;
  const config: ComplianceMonitoredDbConfig = {
    tableName: stringField(record, "tableName", "table_name"),
    region: stringField(record, "region"),
    fields: stringList(record.fields),
    conditions: conditionRecord(record.conditions),
  };
  return config.tableName ||
    config.region ||
    config.fields.length > 0 ||
    Object.keys(config.conditions).length > 0
    ? config
    : null;
}

export function normalizeComplianceRule(row: unknown): ComplianceRule | null {
  if (row == null || typeof row !== "object") {
    return null;
  }
  const record = row as Record<string, unknown>;
  const regulator = stringField(record, "regulator");
  const client = stringField(record, "client");
  const targetAccountId = stringField(
    record,
    "targetAccountId",
    "target_account_id",
  );
  const baselineAccountId = stringField(
    record,
    "baselineAccountId",
    "baseline_account_id",
  );
  const id =
    stringField(record, "id") ||
    [regulator, client, targetAccountId, baselineAccountId]
      .filter(Boolean)
      .join(":");
  if (!id) {
    return null;
  }
  const monitoredWorkloads = arrayField(
    record,
    "monitoredWorkloads",
    "monitored_workloads",
  )
    .map(normalizeMonitoredWorkload)
    .filter((item): item is ComplianceMonitoredWorkload => item != null);
  const monitoredDbConfigs = arrayField(
    record,
    "monitoredDbConfigs",
    "monitored_db_configs",
  )
    .map(normalizeMonitoredDbConfig)
    .filter((item): item is ComplianceMonitoredDbConfig => item != null);
  const indicatorMapping = normalizeIndicatorMapping(
    objectField(record, "indicatorMapping", "indicator_mapping"),
  );
  return {
    id,
    regulator,
    client,
    description: stringField(record, "description"),
    targetAccountId,
    baselineAccountId,
    monitoredWorkloads,
    monitoredDbConfigs,
    indicatorMapping,
    indicators: Object.keys(indicatorMapping),
  };
}

/**
 * The regulator-portal BFF returns the tenant rule as a bare object. The
 * admin-panel list API returns an array. Accept both so a single-row list
 * still works if the envelope ever wraps `data: [...]`.
 */
export function parseComplianceRules(payload: unknown): ComplianceRule[] {
  const fromList = extractFlexibleListRows(payload)
    .map(normalizeComplianceRule)
    .filter((row): row is ComplianceRule => row != null);
  if (fromList.length > 0) {
    return fromList;
  }
  const candidates: unknown[] = [payload];
  if (payload != null && typeof payload === "object" && !Array.isArray(payload)) {
    const data = (payload as Record<string, unknown>).data;
    if (data != null && typeof data === "object" && !Array.isArray(data)) {
      candidates.push(data);
    }
  }
  for (const candidate of candidates) {
    const rule = normalizeComplianceRule(candidate);
    if (rule) {
      return [rule];
    }
  }
  return [];
}

export function complianceRuleFromLocationState(
  state: unknown,
  ruleId: string,
): ComplianceRule | null {
  if (state == null || typeof state !== "object") {
    return null;
  }
  const rule = normalizeComplianceRule((state as { rule?: unknown }).rule);
  return rule?.id === ruleId ? rule : null;
}

export function formatDbConfigConditions(
  conditions: Record<string, string>,
): string {
  const entries = Object.entries(conditions);
  if (entries.length === 0) {
    return "";
  }
  return entries.map(([key, value]) => `${key}=${value}`).join(", ");
}
